import React from 'react';
import {Link} from 'react-router-dom';
import {
  WALKTHROUGH_VIDEO_URL,
  WALKTHROUGH_VIDEO_LENGTH,
  WALKTHROUGH_VIDEO_CHAPTERS,
} from '../domain/videos';

/**
 * Product documentation.
 *
 * Rewritten from the marketing site's version, which described downloading and installing a
 * desktop app. Everything here describes the browser app: project → transcript → word list →
 * convert → import the XML into your editor.
 */

const sections = [
  {id: 'walkthrough', title: 'Video Walkthrough'},
  {id: 'getting-started', title: 'Getting Started'},
  {id: 'mapper', title: 'CensorMe Mapper'},
  {id: 'projects', title: 'Projects'},
  {id: 'transcripts', title: 'Transcripts'},
  {id: 'word-lists', title: 'Word Lists'},
  {id: 'converting', title: 'Converting to XML'},
  {id: 'premiere', title: 'Adobe Premiere Pro'},
  {id: 'batch', title: 'Batch Processing'},
  {id: 'pro-features', title: 'Pro Features'},
  {id: 'limits', title: 'Transcription Limits'},
  {id: 'subscribing', title: 'Subscribing to Pro'},
  {id: 'cancelling', title: 'Cancelling Your Subscription'},
  {id: 'faq', title: 'FAQ'},
];

const faqs = [
  {
    question: 'Which editors can open the export?',
    answer: 'CensorMe exports Final Cut Pro XML (xmeml). Adobe Premiere Pro, DaVinci Resolve and Final Cut Pro 7 / X all import it, as does anything else that accepts an xmeml timeline.',
  },
  {
    question: 'My editor cannot find the audio when I import the XML. What now?',
    answer: 'The XML points at the audio by file path, so a path that is not where your media actually lives '
      + 'leaves the clip unlinked. Premiere Pro and Final Cut mark it offline and finish the import, so you '
      + 'can relink it there; DaVinci Resolve does not flag it, and the usual fix is to add the audio file '
      + 'to your project after importing — it links up as long as the filename still matches. To avoid the '
      + 'step entirely, give the convert dialog the full path to your source audio. CensorMe Mapper also '
      + 'repairs the paths inside an XML you already have, so you do not have to convert again.',
  },
  {
    question: 'Does it delete the words from my audio?',
    answer: 'No. Your media is never altered. CensorMe produces a timeline where the matching words are cut out or replaced, so you can review every edit and undo any of them in your editor.',
  },
  {
    question: 'What happens to my word lists if I cancel Pro?',
    answer: 'Nothing. Your projects, transcripts and word lists stay in your account; you just lose the Pro limits until you subscribe again.',
  },
];

const DocsPage: React.FC = () => {
  const handleAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8 w-full">
      <h1 className="text-4xl font-bold text-white mb-2">Documentation</h1>
      <p className="text-gray-400 mb-8">
                Everything you need to go from raw footage to a censored timeline.
      </p>

      <nav className="mb-12 rounded-lg border border-gray-700 bg-gray-800 p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">On this page</h2>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {sections.map(section => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={event => handleAnchorClick(event, section.id)}
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/*
        First, and above Getting Started, because it answers the same question faster for anyone who
        would rather watch than read. The welcome screen links the same video; both take it from
        domain/videos.ts so the URL is never pasted twice.
      */}
      <section id="walkthrough" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Video Walkthrough</h2>
        <a
          href={WALKTHROUGH_VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group block rounded-lg border border-primary-500/40 bg-gray-800 p-6
           transition-colors hover:border-primary-500/70 hover:bg-gray-700/40"
        >
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full
             bg-primary-500 text-white transition-colors group-hover:bg-primary-400">
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="ml-0.5 h-5 w-5"
                aria-hidden="true"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
            <div className="min-w-0">
              <p className="text-lg font-semibold text-white">
                Watch the whole workflow
                <span className="ml-2 text-sm font-normal tabular-nums text-gray-400">
                  {WALKTHROUGH_VIDEO_LENGTH}
                </span>
              </p>
              <p className="mt-1 text-gray-300 leading-relaxed">
                Everything on this page, demonstrated end to end. Opens in a new tab.
              </p>
            </div>
          </div>
        </a>
        <p className="text-gray-300 leading-relaxed mt-4 mb-2">It covers:</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
          {WALKTHROUGH_VIDEO_CHAPTERS.map(chapter => (
            <li key={chapter}>{chapter}</li>
          ))}
        </ul>
      </section>

      <section id="getting-started" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Getting Started</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          <Link to="/signup" className="text-blue-400 underline hover:text-blue-300">Start a trial</Link>{' '}
                    with your card details, click the sign-in link we email you, choose a password, and you land
                    on the welcome screen.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">The whole workflow is four steps:</p>
        <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-6">
          <li>Create a project (or work in the Default Project).</li>
          <li>Add a transcript — a timestamped, word-level CSV of your audio.</li>
          <li>Pick the word list that decides what gets cut.</li>
          <li>Convert, download the XML, and import it into your editor.</li>
        </ol>
      </section>

      <section id="mapper" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">CensorMe Mapper</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          <Link to="/downloads" className="text-blue-400 underline hover:text-blue-300">CensorMe Mapper</Link>
          {' '}is the free desktop companion app for Windows and macOS. It does two things the browser
                    cannot:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
          <li>
            <strong className="text-white">Repair the media paths in an XML.</strong> It rewrites the file
                        paths inside a converted XML to point at your media where it actually lives, so your
                        editor relinks the audio instead of hunting for it. No need to convert again.
          </li>
          <li>
            <strong className="text-white">Upload audio to be transcribed.</strong> Sign in with the same
                        account and send files straight from your desktop. They land in your workspace here,
                        ready to transcribe and convert like anything else you uploaded.
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed">
                    It is also the quickest fix for an import where your editor cannot find the audio — see the
          {' '}<a href="#faq" onClick={event => handleAnchorClick(event, 'faq')} className="text-blue-400 underline hover:text-blue-300">FAQ</a>.
        </p>
      </section>

      <section id="projects" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Projects</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    A project groups the transcripts, conversions and default word list for one piece of work —
                    an episode, a series, a client. Switch between projects from the header, star the ones you
                    use most, and they stay pinned to the top of the switcher.
        </p>
        <p className="text-gray-300 leading-relaxed">
          <strong className="text-white">The Default Project</strong> is the catch-all project. Anything you
                    create there is not tied to a specific piece of work, which is handy for word lists you want
                    everywhere.
        </p>
      </section>

      <section id="transcripts" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Transcripts</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    A transcript is a CSV with one row per spoken word and a start and end timestamp. That
                    timing is what lets CensorMe cut precisely around a word instead of a whole sentence.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">There are two ways to get one:</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
          <li>
            <strong className="text-white">Transcribe your media.</strong> Upload an audio or video file
                        and CensorMe transcribes it for you. Longer files are queued — the header shows a spinner
                        while a job is running, and clicking it takes you back to the page that started it.
          </li>
          <li>
            <strong className="text-white">Upload a CSV you already have.</strong> If you have already
                        transcribed elsewhere, upload the CSV directly and skip straight to converting.
          </li>
        </ul>
      </section>

      <section id="word-lists" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Word Lists</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    A word list is what CensorMe looks for. Matching is case-insensitive, so you only need each
                    word once.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">You can:</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
          <li>Build lists by hand, and search within a list once it gets long.</li>
          <li>Browse the marketplace and add public or official lists to your collection.</li>
          <li>Publish your own lists so other users can add them.</li>
          <li>Set a default list per project so it is pre-selected every time you convert.</li>
        </ul>
        <h3 className="text-xl font-semibold text-white mb-3">Importing</h3>
        <p className="text-gray-300 leading-relaxed mb-4">Two formats are accepted:</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
          <li>
            <code className="text-primary-300">.csv</code> — one list per line, in the form
            {' '}<code className="text-primary-300">listName,word,word,word</code>.
          </li>
          <li>
            <code className="text-primary-300">.txt</code> — one word per line; the filename becomes the
                        list name.
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed">
                    Importing a list whose name already exists merges the two and removes duplicates rather than
                    creating a second copy.
        </p>
      </section>

      <section id="converting" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Converting to XML</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    Pick a transcript, pick a word list, and set the options:
        </p>
        <p className="text-gray-300 leading-relaxed">
                    Every conversion is saved to your history, so you can re-download an XML later without
                    converting again.
        </p>
      </section>
      <section id="premiere" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Adobe Premiere Pro</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    Premiere imports the XML without complaining, even when the media path inside it points
                    somewhere your audio is not. It does not flag the clip as offline and it does not stop to
                    ask — the sequence opens, every cut is in the right place, and the only sign anything is
                    wrong is that the clips are not playing your audio and the timeline is red.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
                    So pointing the sequence at the real file is a step you do yourself. Either way works:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
          <li>
            <strong className="text-white">Run the XML through the Mapper first.</strong>{' '}
            <Link to="/downloads" className="text-blue-400 underline hover:text-blue-300">CensorMe Mapper</Link>
            {' '}rewrites the paths to point at your media, so the import comes in already linked.
          </li>
          <li>
            <strong className="text-white">Or remap it in Premiere.</strong> Right-click the source clip in
                        the project panel and use <strong className="text-white">Replace Footage</strong> to
                        point it at the correct file. Every cut made from that source follows it.
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed">
                    Giving the convert dialog the real path to your source audio avoids the whole thing.
        </p>
      </section>

      <section id="batch" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Batch Processing</h2>
        <p className="text-gray-300 leading-relaxed">
                    Batch transcribe queues several files at once; batch convert runs one word list across
                    several transcripts and downloads an XML for each. Both are under <strong className="text-white">Batch</strong>
          {' '}in the header. Your browser handles duplicate filenames in the download folder.
        </p>
      </section>

      <section id="pro-features" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Pro Features</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    Every account is on Pro — a trial starts you there, and the card details are collected up
                    front. That covers:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6">
          <li>25 hours of transcription per month</li>
          <li>Files up to 4 hours long</li>
          <li>Priority support</li>
        </ul>
      </section>

      {/*
        The numbers here are the `pro` row of the backend's plan_limits table — 90000s total,
        14400s per session, 14400s of session duration. They are hardcoded, like the Pro Features
        list above, so they have to be re-checked against that table if a plan is ever repriced.
      */}
      <section id="limits" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Transcription Limits</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Transcription is the only metered part of CensorMe. Uploading audio and converting a
          transcript to XML are free and unlimited — neither one counts against anything below, and
          you can re-convert the same transcript with a different word list as often as you like.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          There are two separate meters, and a transcription has to clear both:
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-6 mb-4">
          <li>
            <strong className="text-white">A monthly allowance</strong> — 25 hours of audio per
            billing period, which resets when the period rolls over.
          </li>
          <li>
            <strong className="text-white">A rolling session window</strong> — at most 4 hours of
            audio within any 4-hour window. The window opens the moment you start a transcription
            with none already running, and restarts 4 hours later.
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed mb-4">
          The second is what paces a heavy day: you can have 20 hours left this month and still not
          be able to start a fifth hour right now. It is a wait rather than a bill —{' '}
          <strong className="text-white">upgrading does not shorten it</strong>, because the window
          is the same on every plan. When you hit it, CensorMe offers to hold the file and start it
          automatically as soon as the window resets, so you do not have to come back and remember.
        </p>
        <h3 className="text-xl font-semibold text-white mb-3">Studio</h3>
        <p className="text-gray-300 leading-relaxed">
          A Studio plan with higher limits is planned. If you are regularly running into the
          ceiling today,{' '}
          <Link to="/contact" className="text-blue-400 underline hover:text-blue-300">get in touch</Link>
          {' '}— we would like to hear what your volume looks like.
        </p>
      </section>

      <section id="subscribing" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Subscribing to Pro</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    Pick monthly or yearly on the <Link to="/#pricing" className="text-blue-400 underline hover:text-blue-300">pricing section</Link>
          {' '}or from your account page, and complete checkout. Payment is handled by Paddle, our
                    merchant of record.
        </p>
        <p className="text-gray-300 leading-relaxed">
                    Your plan is active as soon as the payment clears. If the badge has not updated, reload the
                    account page.
        </p>
      </section>

      <section id="cancelling" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">Cancelling Your Subscription</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
                    Open your account page and use <strong className="text-white">Cancel Subscription</strong> in
                    the subscription management card. That opens Paddle&apos;s hosted cancellation page. The same
                    card has a link to update your payment method.
        </p>
        <p className="text-gray-300 leading-relaxed">
                    Your Pro access continues until the end of the period you have already paid for. EU and UK
                    consumers have an additional 14-day right to cancel — see the{' '}
          <Link to="/terms" className="text-blue-400 underline hover:text-blue-300">Terms of Service</Link>.
        </p>
      </section>

      <section id="faq" className="mb-12 scroll-mt-24">
        <h2 className="text-2xl font-bold text-white mb-4">FAQ</h2>
        <div className="space-y-6">
          {faqs.map(faq => (
            <div key={faq.question}>
              <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
              <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12 pt-8 border-t border-gray-700">
        <p className="text-gray-300">
                    Still stuck? <Link to="/contact" className="text-blue-400 underline hover:text-blue-300">Get in touch</Link>
          {' '}and we will help.
        </p>
      </section>
    </div>
  );
};

export default DocsPage;
